// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';

// import third-party libraries
import {
  AtomicBlockUtils
  , CompositeDecorator
  , ContentState
  , convertFromRaw
  , convertToRaw
  , Editor
  , EditorState
  , RichUtils
} from 'draft-js';
import classNames from 'classnames';

// import components
import Base from "../BaseComponent.js.jsx";
import BasicBlockStyleControls from './editorComponents/BasicBlockStyleControls.js.jsx';
import FullBlockStyleControls from './editorComponents/FullBlockStyleControls.js.jsx';
import LimitedBlockStyleControls from './editorComponents/LimitedBlockStyleControls.js.jsx';
import LinkControls from './editorComponents/LinkControls.js.jsx';
import InlineStyleControls from './editorComponents/InlineStyleControls.js.jsx';
import MediaControls from './editorComponents/MediaControls.js.jsx';

const styles = {
  root: {
    fontFamily: '\'Georgia\', serif'
    , padding: 20
    , width: 600
  },
  buttons: {
    marginBottom: 10
  },
  urlInputContainer: {
    marginBottom: 10
  },
  urlInput: {
    fontFamily: '\'Georgia\', serif'
    , marginRight: 10
    , padding: 3
    , width: '30%'
  },
  editor: {
    border: '1px solid #ccc'
    , cursor: 'text'
    , minHeight: 80
    , padding: 10
  },
  button: {
    marginTop: 10
    , textAlign: 'center'
  },
  link: {
    color: '#3b5998'
    , textDecoration: 'underline'
  },
  media: {
    width: '100%',
    // Fix an issue with Firefox rendering video controls
    // with 'pre-wrap' white-space
    whiteSpace: 'initial'
  },
};

class RichEditor extends Base {

  constructor(props) {
    super(props);

    // new ***********
    let editorState;
    const Link = (props) => {
      const {url} = props.contentState.getEntity(props.entityKey).getData();
      return (
        <a href={url} style={styles.link}>
          {props.children}
        </a>
      );
    };
    const decorator = new CompositeDecorator([
      {
        strategy: this._findLinkEntities
        , component: Link
      }
    ]);
    if(this.props.value.blocks.length < 1) {
      editorState = EditorState.createEmpty();
    } else {
      let rawContent = Object.assign({}, this.props.value);
      // console.log(rawContent);
      if(!rawContent.entityMap) {
        rawContent.entityMap = {};
      }
      editorState = EditorState.createWithContent(convertFromRaw(rawContent), decorator);
    }
    // console.log(editorState);

    // original ***********
    // let rawContent = Object.assign({}, this.props.value);
    // // console.log(rawContent);
    // if(!rawContent.entityMap) {
    //   rawContent.entityMap = {};
    // }
    // let editorState = EditorState.createWithContent(convertFromRaw(rawContent));
    this.state = {
      editorState: editorState // EditorState.createEmpty(decorator)
      , mediaUrlValue: ''
      , placeholder: this.props.placeholder ? this.props.placeholder : ''
      , showURLInput: false
      , showURLMediaInput: false
      , urlValue: ''
    }
    this._bind(
      '_cancelLink'
      , '_cancelMediaLink'
      , '_confirmLink'
      , '_confirmMedia'
      , '_findLinkEntities'
      , '_onLinkInputKeyDown'
      , '_onMediaURLChange'
      , '_onURLChange'
      , '_promptForLink'
      , '_removeLink'
      , 'onChange'
    );

    this.handleKeyCommand = (command) => this._handleKeyCommand(command);
    this.onTab = (e) => this._onTab(e);
    this.onFocus = (e) => this._onFocus(e);
    this.onBlur = (e) => this._onBlur(e);
    this.toggleBlockType = (type) => this._toggleBlockType(type);
    this.toggleInlineStyle = (style) => this._toggleInlineStyle(style);
    this.toggleMediaFile = (file) => this._toggleMedialFile(file);
  }

  _onURLChange(e) {
    this.setState({urlValue: e.target.value});
  }

  _onMediaURLChange(e) {
    this.setState({mediaUrlValue: e.target.value});
  }

  _findLinkEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === 'LINK'
        );
      },
      callback
    );
  }

  _promptForLink(e) {
    e.preventDefault();
    console.log("HELLO FROM _promptForLink");
    const {editorState} = this.state;
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = editorState.getSelection().getStartKey();
      const startOffset = editorState.getSelection().getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      let url = '';
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        url = linkInstance.getData().url;
      }
      this.setState({
        showURLInput: true,
        urlValue: url,
      }, () => {
        setTimeout(() => this.refs.url.focus(), 0);
      });
    }
  }

  _confirmLink(e) {
    e.preventDefault();
    const { editorState, urlValue } = this.state;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'LINK',
      'MUTABLE',
      {url: urlValue}
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
    this.setState({
      editorState: RichUtils.toggleLink(
        newEditorState,
        newEditorState.getSelection(),
        entityKey
      ),
      showURLInput: false,
      urlValue: '',
    }, () => {
      setTimeout(() => this.refs.editor.focus(), 10);
    });
  }

  _confirmMedia(e) {
    e.preventDefault();
    const { editorState, mediaUrlValue } = this.state;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'image',
      'IMMUTABLE',
      {src: mediaUrlValue}
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {currentContent: contentStateWithEntity});
    this.setState({
      editorState: AtomicBlockUtils.insertAtomicBlock(
        newEditorState,
        entityKey,
        ' '
      ),
      showURLMediaInput: false,
      mediaUrlValue: '',
    }, () => {
      setTimeout(() => this.refs.editor.focus(), 10);
    });
  }

  _cancelLink(e) {
    e.preventDefault();
    this.setState({
      showURLInput: false,
      urlValue: '',
    }, () => {
      setTimeout(() => this.refs.editor.focus(), 10);
    });
  }

  _cancelMediaLink(e) {
    e.preventDefault();
    this.setState({
      showURLMediaInput: false,
      mediaUrlValue: '',
    }, () => {
      setTimeout(() => this.refs.editor.focus(), 10);
    });
  }

  _onLinkInputKeyDown(e) {
    if (e.which === 13) {
      this._confirmLink(e);
    }
  }

  _removeLink(e) {
    e.preventDefault();
    console.log("HELLO FROM _removeLink");
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      this.setState({
        editorState: RichUtils.toggleLink(editorState, selection, null),
      });
    }
  }

  _handleKeyCommand(command) {
    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.onChange(newState);
      return true;
    }
    return false;
  }

  _onTab(e) {
    const maxDepth = 4;
    this.onChange(RichUtils.onTab(e, this.state.editorState, maxDepth));
  }

  _onFocus(e) {
    // console.log("focus")
    this.setState({placeholder: null});
  }

  _onBlur(e) {
    // console.log("blur");
    let contentState = this.state.editorState.getCurrentContent();
    let placeholder = this.props.placeholder ? this.props.placeholder : 'Rich content goes here...';
    if (!contentState.hasText()) {
      this.setState({placeholder})
    }
  }

  _toggleBlockType(blockType) {
    this.onChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType
      )
    );
  }

  _toggleInlineStyle(inlineStyle) {
    this.onChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        inlineStyle
      )
    );
  }

  _toggleMedialFile(file) {
    this.setState({
      showURLMediaInput: !this.state.showURLMediaInput
    })
  }

  onChange(editorState) {
    //spit back out raw content
    // console.log("CHANGE");
    this.setState({editorState});
    // console.log(e);
    const newRawContent = convertToRaw(editorState.getCurrentContent());
    this.props.change({
      target: {
        name: this.props.name
        , value: newRawContent
      }
    });

  }

    // check the props the component receives
  componentWillReceiveProps(nextProps) {
    // console.log("RICHEDITOR change props");
    // console.log(nextProps);

    // CHECK IF IT'S NEW.
    // - if it is, reset the editor state to empty.  (this wasn't happening before)
    // console.log("NEXT PROPS!!!");
    // console.log(nextProps);
    // console.log(nextProps.isNewContent);

    // console.log(nextProps);

    if(nextProps.isNewContent) {

      let rawContent = Object.assign({}, nextProps.value);
      // console.log(rawContent);
      if(!rawContent.entityMap) {
        rawContent.entityMap = {};
      }
      // console.log("CREATE WITH CONTENT");
      // console.log(rawContent);
      let editorState = EditorState.createWithContent(convertFromRaw(rawContent));
      // console.log("editor state", editorState);

      this.setState({editorState});

    }
  }

  render() {
    const {
      allowLinks
      , allowMedia
      , controls
      , helpText
      , label
      , name
      , required
      , value
    } = this.props;

    // https://github.com/facebook/draft-js/blob/master/examples/rich/rich.html
    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    // let className = 'RichEditor-editor';
    let hidePlaceholder = false;
    let contentState = this.state.editorState.getCurrentContent();
    // console.log("++++++++++ contentState ++++++++++++");
    // console.log(this.state.editorState.toJS());
    let selection = this.state.editorState.getSelection();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        hidePlaceholder = true;
      }
    }

    let editorClass = classNames(
      'RichEditor-editor'
      , { 'RichEditor-hidePlaceholder': hidePlaceholder }
    )

    let rootClass = classNames(
      'RichEditor-root'
      , { 'hasFocus': selection.hasFocus }
    )

    let urlInput = <div/>;
    if (this.state.showURLInput) {
      urlInput =
        <div style={styles.urlInputContainer}>
          <input
            onChange={this._onURLChange}
            ref="url"
            style={styles.urlInput}
            type="text"
            value={this.state.urlValue}
            onKeyDown={this._onLinkInputKeyDown}
          />
          <button type="button" className="yt-btn x-small" onClick={this._confirmLink}>
            Confirm
          </button>
          <button type="button" className="yt-btn x-small link" onClick={this._cancelLink}>
            Cancel
          </button>
        </div>;
    }

    let urlMediaInput = <div/>;
    if (this.state.showURLMediaInput) {
      urlMediaInput =
        <div style={styles.urlInputContainer}>
          <input
            onChange={this._onMediaURLChange}
            ref="url"
            style={styles.urlInput}
            type="text"
            value={this.state.mediaUrlValue}
            onKeyDown={this._onLinkInputKeyDown}
          />
          <button type="button" className="yt-btn x-small" onClick={this._confirmMedia}>
            Confirm
          </button>
          <button type="button" className="yt-btn x-small link" onClick={this._cancelMediaLink}>
            Cancel
          </button>
        </div>;
    }

    return (
      <div className="input-group" style={{minHeight:'100px'}}>
        <label >{label} {required ? <sup className="-required">*</sup> : null}</label>
        <div className={rootClass} >
          { controls === 'full' ?
            <FullBlockStyleControls
              editorState={this.state.editorState}
              onToggle={this.toggleBlockType}
              promptForLink={this._promptForLink}
              removeLink={this._removeLink}
            />
            : controls === 'none' || controls === 'basic' ?
              <BasicBlockStyleControls
                editorState={this.state.editorState}
                onToggle={this.toggleBlockType}
                promptForLink={this._promptForLink}
                removeLink={this._removeLink}
              />
            :
            <LimitedBlockStyleControls
              editorState={this.state.editorState}
              onToggle={this.toggleBlockType}
              promptForLink={this._promptForLink}
              removeLink={this._removeLink}
            />
          }
          <InlineStyleControls
            editorState={this.state.editorState}
            onToggle={this.toggleInlineStyle}
          />
          { allowLinks ?
            <LinkControls
              editorState={this.state.editorState}
              onToggle={this.toggleMediaFile}
            />
            :
            null
          }
          { allowMedia ?
            <MediaControls
              editorState={this.state.editorState}
              onToggle={this.toggleMediaFile}
            />
            :
            null
          }
          {urlInput}
          {urlMediaInput}
          <div className={editorClass} onClick={this.focus}>
            <Editor
              blockRendererFn={mediaBlockRenderer}
              blockStyleFn={getBlockStyle}
              customStyleMap={styleMap}
              editorState={this.state.editorState}
              handleKeyCommand={this.handleKeyCommand}
              onBlur={this.onBlur}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onTab={this.onTab}
              placeholder={this.state.placeholder}
              ref="editor"
            />
          </div>
        </div>
        <small className="help-text"><em>{helpText}</em></small>
      </div>
    )

  }
}

//additional functions from facebook example:
//(similar to what we have already in carbon, but I didn't want to copy those because some things have changed in draftjs)
// this form should be self contained and re-useable.

const styleMap = {
  //override styles things if we want
};

function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote': return 'RichEditor-blockquote';
    default: return null;
  }
}

function mediaBlockRenderer(block) {
  if(block.getType() === 'atomic') {
    return {
      component: Media
      , editable: false
    }
  }
  return null;
}

const Image = (props) => {
  return <img src={props.src} style={styles.media} />;
};

const Media = (props) => {
  const entity = props.contentState.getEntity(
    props.block.getEntityAt(0)
  );
  const {src} = entity.getData();
  const type = entity.getType();
  let media;
  if (type === 'audio') {
    media = <Audio src={src} />;
  } else if (type === 'image') {
    media = <Image src={src} />;
  } else if (type === 'video') {
    media = <Video src={src} />;
  }
  return media;
};

RichEditor.propTypes = {
  allowLinks: PropTypes.bool
  , allowMedia: PropTypes.bool
  , change: PropTypes.func.isRequired
  , controls: PropTypes.string
  , helpText: PropTypes.any
  , label: PropTypes.string
  , name: PropTypes.string.isRequired
  , placeholder: PropTypes.string
  , required: PropTypes.bool
  , value: PropTypes.object
}

RichEditor.defaultProps = {
  allowLinks: false
  , allowMedia: false
  , controls: 'limited'
  , helpText: ''
  , label: ''
  , placeholder: ''
  , required: false
  , value: {}
}

export default RichEditor;



// WEBPACK FOOTER //
// global/components/forms/RichEditor.js.jsx